import { useState, useEffect, useRef, useCallback } from 'react'
import { useNotes } from './hooks/useNotes'
import { useNotePhotos } from './hooks/useNotePhotos'
import { useAuth } from '../../hooks/useAuth'
import { LightboxViewer } from '../../components/LightboxViewer'
import { getSignedUrl } from '../../lib/upload'
import { SkeletonList } from '../../components/ui/Skeleton'
import { EmptyState }  from '../../components/ui/EmptyState'
import { Button } from '../../components/ui/Button'
import { SearchBar } from '../../components/ui/SearchBar'
import { cn } from '../../lib/utils'

// ─── Rich text toolbar ────────────────────────────────────────────────────────
const Toolbar = ({ editorRef }) => {
  const cmd = (command, value) => {
    editorRef.current?.focus()
    document.execCommand(command, false, value)
  }
  return (
    <div className="flex gap-1 px-3 py-2 border-b border-border">
      <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('bold') }}
        className="w-7 h-7 flex items-center justify-center rounded text-text-secondary hover:bg-background font-bold text-sm">B</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('italic') }}
        className="w-7 h-7 flex items-center justify-center rounded text-text-secondary hover:bg-background italic text-sm">I</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('insertUnorderedList') }}
        className="w-7 h-7 flex items-center justify-center rounded text-text-secondary hover:bg-background text-sm">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" />
          <circle cx="4" cy="6" r="1.5" fill="currentColor" /><circle cx="4" cy="12" r="1.5" fill="currentColor" /><circle cx="4" cy="18" r="1.5" fill="currentColor" />
        </svg>
      </button>
    </div>
  )
}

// ─── Photo grid (thumbnails) ──────────────────────────────────────────────────
const PhotoGrid = ({ photos, onPhotoClick }) => {
  const [thumbUrls, setThumbUrls] = useState({})

  const loadThumb = async (photo) => {
    if (thumbUrls[photo.id]) return
    try {
      const thumbPath = photo.file_path.replace(/(\.[^.]+)?$/, '_thumb.jpg')
      const url = await getSignedUrl('household-photos', thumbPath)
      setThumbUrls((prev) => ({ ...prev, [photo.id]: url }))
    } catch {
      try {
        const url = await getSignedUrl('household-photos', photo.file_path)
        setThumbUrls((prev) => ({ ...prev, [photo.id]: url }))
      } catch {}
    }
  }

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
      {photos.map((photo, i) => {
        if (!thumbUrls[photo.id]) loadThumb(photo)
        return (
          <button
            key={photo.id}
            onClick={() => onPhotoClick(i)}
            className="aspect-square rounded-lg overflow-hidden bg-border/30 relative group"
          >
            {thumbUrls[photo.id] ? (
              <img src={thumbUrls[photo.id]} alt={photo.file_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {!navigator.onLine ? (
                  <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-border border-t-primary animate-spin" />
                )}
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

const UploadingGrid = ({ count }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 mt-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="aspect-square rounded-lg bg-border/30 flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    ))}
  </div>
)

// ─── Note editor ──────────────────────────────────────────────────────────────
const NoteEditor = ({ note, onSave, onClose }) => {
  const { user, isLimited } = useAuth()
  const { data: photos = [], addPhotos, updateCaption, removePhoto } = useNotePhotos(note?.id)
  const editorRef    = useRef(null)
  const titleRef     = useRef(null)
  const saveTimer    = useRef(null)
  const fileInputRef = useRef(null)
  const [title,          setTitle]          = useState(note?.title ?? '')
  const [isShared,       setIsShared]       = useState(note?.is_shared ?? false)
  const [saveState,      setSaveState]      = useState('idle') // idle | saving | saved
  const [lightboxIndex,  setLightboxIndex]  = useState(null)
  const [uploading,      setUploading]      = useState(0)

  useEffect(() => {
    if (editorRef.current && note?.content) {
      editorRef.current.innerHTML = note.content
    }
    titleRef.current?.focus()
  }, [])

  const triggerSave = useCallback((currentTitle, currentShared) => {
    clearTimeout(saveTimer.current)
    setSaveState('saving')
    saveTimer.current = setTimeout(async () => {
      const content = editorRef.current?.innerHTML ?? ''
      await onSave({ title: currentTitle, content, is_shared: currentShared })
      setSaveState('saved')
      setTimeout(() => setSaveState('idle'), 2000)
    }, 800)
  }, [onSave])

  const handleTitleChange = (e) => {
    setTitle(e.target.value)
    triggerSave(e.target.value, isShared)
  }

  const handleContentInput = () => {
    triggerSave(title, isShared)
  }

  const toggleShared = () => {
    const next = !isShared
    setIsShared(next)
    triggerSave(title, next)
  }

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files ?? []).slice(0, 20)
    if (!files.length) return
    setUploading(files.length)
    try {
      await addPhotos.mutateAsync(files)
    } catch (err) {
      console.error(err)
    } finally {
      setUploading(0)
      e.target.value = ''
    }
  }

  const isOwner = !note || note.created_by === user?.id

  return (
    <>
      <div className="fixed inset-0 z-50 flex flex-col bg-surface">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-border flex-shrink-0">
          <button onClick={onClose} className="flex items-center gap-1.5 text-text-muted hover:text-text-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
          </button>
          <div className="text-xs text-text-muted">
            {saveState === 'saving' && 'Saving…'}
            {saveState === 'saved'  && <span className="text-emerald-600">Saved</span>}
          </div>
          {isOwner && (
            <button onClick={toggleShared}
              className={cn('text-xs font-medium px-3 py-1 rounded-full border transition-colors',
                isShared ? 'bg-primary text-white border-primary' : 'bg-background text-text-muted border-border')}>
              {isShared ? 'Shared' : 'Private'}
            </button>
          )}
        </div>

        {/* Title */}
        <input
          ref={titleRef}
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Title"
          className="px-5 py-4 text-2xl font-bold text-text-primary placeholder:text-border focus:outline-none flex-shrink-0"
        />

        {/* Toolbar */}
        <Toolbar editorRef={editorRef} />

        {/* Scrollable body + photos */}
        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          <div
            ref={editorRef}
            contentEditable={true}
            suppressContentEditableWarning={true}
            onInput={handleContentInput}
            className="px-5 py-4 text-sm text-text-primary focus:outline-none prose prose-sm max-w-none min-h-[160px]"
            data-placeholder="Start writing…"
          />

          {/* Photos section */}
          <div className="px-5 pb-6 border-t border-border mt-2 pt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                Photos{photos.length > 0 && ` · ${photos.length}`}
              </span>
              {!isLimited && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1 text-xs text-primary font-medium"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                      <circle cx="12" cy="13" r="3"/>
                    </svg>
                    Add photos
                  </button>
                </>
              )}
            </div>

            {photos.length > 0 && (
              <PhotoGrid
                photos={photos}
                onPhotoClick={(i) => setLightboxIndex(i)}
              />
            )}
            {uploading > 0 && <UploadingGrid count={uploading} />}
            {photos.length === 0 && uploading === 0 && (
              <p className="text-xs text-text-muted italic">No photos attached.</p>
            )}
          </div>
        </div>
      </div>

      {lightboxIndex !== null && (
        <LightboxViewer
          photos={photos}
          initialIndex={lightboxIndex}
          bucket="household-photos"
          userId={user?.id}
          isAdmin={false}
          onClose={() => setLightboxIndex(null)}
          onDelete={(id) => removePhoto.mutateAsync({ id, filePath: photos.find((p) => p.id === id)?.file_path })}
          onEditCaption={(id, caption) => updateCaption.mutateAsync({ id, caption })}
        />
      )}
    </>
  )
}

// ─── Note card ────────────────────────────────────────────────────────────────
const NoteCard = ({ note, onOpen, onDelete }) => {
  const [confirmDel, setConfirmDel] = useState(false)
  const firstLine = note.content
    ? note.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80)
    : ''

  return (
    <div className="bg-surface rounded-xl border border-border p-4 cursor-pointer hover:border-primary/30 transition-colors"
      onClick={() => onOpen(note)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-text-primary truncate">{note.title || 'Untitled'}</div>
          {firstLine && <div className="text-xs text-text-muted mt-0.5 truncate">{firstLine}</div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {note.is_shared && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-primary-light text-primary rounded-full">Shared</span>
          )}
          {confirmDel ? (
            <>
              <button onClick={() => setConfirmDel(false)} className="text-xs text-text-muted">Cancel</button>
              <button onClick={() => onDelete(note.id)} className="text-xs text-rose-500 font-semibold">Delete</button>
            </>
          ) : (
            <button onClick={() => setConfirmDel(true)} className="text-text-muted hover:text-rose-500 p-1">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export const NotesPage = () => {
  const { user }                                          = useAuth()
  const { data: notes = [], isLoading, createNote, updateNote, deleteNote } = useNotes()
  const [editing, setEditing] = useState(null) // null | { note } | { isNew: true }
  const [search,  setSearch]  = useState('')

  const searchFilter = (n) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return n.title?.toLowerCase().includes(q) || n.content?.toLowerCase().includes(q)
  }

  const personalNotes = notes.filter((n) => !n.is_shared && n.created_by === user?.id).filter(searchFilter)
  const sharedNotes   = notes.filter((n) => n.is_shared).filter(searchFilter)

  const openNew = async () => {
    const note = await createNote({ title: '', content: '', is_shared: false })
    setEditing(note)
  }

  const handleSave = async ({ title, content, is_shared }) => {
    if (!editing?.id) return
    await updateNote({ id: editing.id, title, content, is_shared })
    setEditing((prev) => prev ? { ...prev, title, content, is_shared } : prev)
  }

  if (isLoading) return <div className="px-4 py-5"><SkeletonList count={5} /></div>

  if (editing) {
    return (
      <NoteEditor
        note={editing}
        onSave={handleSave}
        onClose={() => setEditing(null)}
      />
    )
  }

  const Section = ({ title, items }) => {
    if (items.length === 0) return null
    return (
      <div className="mb-6">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">{title}</h2>
        <div className="space-y-2">
          {items.map((note) => (
            <NoteCard key={note.id} note={note} onOpen={setEditing} onDelete={deleteNote} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-text-primary">Notes</h1>
        <button onClick={openNew}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          New Note
        </button>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search notes…" className="mb-4" />

      {notes.length === 0 ? (
        <EmptyState
          type="notes"
          title="No notes yet"
          description="Tap New Note to capture ideas, reminders, or anything your household needs to remember."
        />
      ) : (
        <>
          <Section title="Personal" items={personalNotes} />
          <Section title="Shared" items={sharedNotes} />
        </>
      )}
    </div>
  )
}
